import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  type Collision,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FolderPlus,
  History,
  ListOrdered,
  SlidersHorizontal,
} from 'lucide-react';
import { z } from 'zod';
import {
  type AbilitySummary,
  PERSONAL_SPACE_MAX_TASKS,
  type Permission,
  ReplayView,
  directoryGroupOf,
  orderRows,
  periodOf,
  periodRange,
} from '@apm/contracts';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/states';
import { CreateProjectDialog } from '../../features/projects/components/create-project-dialog';
import { GLASS_TAB_TRIGGER_CLASS, Tabs } from '../../components/ui/tabs';
import { boardApi, boardKeys } from '../../features/board/api';
import { CoordinationPanel } from '../../features/coordination/components/coordination-panel';
import { ChangeRequestsPanel } from '../../features/change-requests/components/change-requests-panel';
import { OverviewPanel } from '../../features/overview/components/overview-panel';
import { IssuePanel } from '../../features/issues/components/issue-panel';
import { RaidPanel } from '../../features/raid/components/raid-panel';
import { ReplayPanel } from '../../features/replay/components/replay-panel';
import { ProjectSettingsPanel } from '../../features/projects/components/project-settings-panel';
import { ProjectFilesPage } from '../../features/files/components/project-files-page';
import {
  DECISION_SHEET_MODES,
  DecisionSheetPage,
} from '../../features/decision-sheets/components/decision-sheet-page';
import { ProjectDirectoryCompact } from '../../features/projects/components/project-directory-compact';
import { PersonalSpaceCard } from '../../features/organizations/components/personal-space-card';
import { projectApi, projectKeys } from '../../features/projects/api';
import { switchOrgAndGo } from '../../features/projects/elsewhere';
import { savedHiddenIds, visibleOnSidebar } from '../../features/projects/sidebar-projects';
import {
  DIR_DRAG_PREFIX,
  ProjectDirectoryScreen,
  directorySequence,
  projectIdOfDrag,
} from '../../features/projects/components/project-directory-screen';
import { ProjectTabLabel } from '../../features/projects/components/project-tab-label';
import { ProjectTabsRow } from '../../features/projects/components/project-tabs-row';
import {
  ProjectDrawer,
  ProjectSwitcherButton,
  useNarrowScreen,
} from '../../features/projects/components/project-drawer';
import { ProjectTabLayoutSection } from '../../features/projects/components/project-tab-layout-section';
import { Dialog } from '../../components/ui/dialog';
import { tabColorClass } from '../../features/projects/project-color';
import { SetupBadge } from '../../features/projects/components/setup-badge';
import {
  ProjectSidebar,
  SIDEBAR_DROP_ID,
} from '../../features/projects/components/project-sidebar';
import { initialOrder, moveBefore, moveToward } from '../../features/projects/sidebar-arrange';
import {
  UTILITY_TAB_IDS,
  isBlockedInPersonalSpace,
  isProjectTabAvailable,
  visibleProjectTabs,
} from '../../features/projects/tab-registry';
import { useOptionalUser } from '../../features/auth/auth-context';
import { useOrgIsPersonal } from '../../features/auth/use-org-is-personal';
import { useShellMode } from '../../lib/shell-mode';
import { SprintTabPanel } from '../../features/sprints/components/sprint-tab-panel';
import { TASK_VIEWS, TasksPanel } from '../../features/tasks/components/tasks-panel';
import {
  LEGACY_REPORT_GROUPS,
  REPORT_GROUPS,
  ReportsPanel,
} from '../../features/reports/components/reports-panel';
import { BudgetPanel } from '../../features/budget/components/budget-panel';
import { TestingPanel } from '../../features/testing/components/testing-panel';
import { DesignPanel } from '../../features/design/components/design-panel';
import { GitPanel } from '../../features/git/components/git-panel';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { errorMessage } from '../../lib/error-message';
import { apiFetch } from '../../lib/http';

/**
 * Tab và project đang chọn nằm ở **search param**, không phải `useState`.
 *
 * docs/frontend-guide.md §2: link tới một board hay một backlog cụ thể phải
 * chia sẻ được. Chôn vào state thì mọi người mở link đều rơi về mặc định.
 */
export const ProjectSearch = z.object({
  projectId: z.string().optional(),
  /**
   * Mục đang mở trong tab Cấu hình — APM-PSET-a.
   *
   * `z.string()` là đúng dù router phân tích search param bằng `JSON.parse`: id
   * ở đây là `members`, `definition-of-done`… — không chuỗi nào trong số đó là
   * JSON hợp lệ, nên chúng tới schema nguyên dạng chuỗi.
   *
   * Không dùng `z.enum(...)`: id lạ (link cũ, gõ tay) phải rơi về danh sách mục
   * chứ không làm hỏng cả route, và `settingsSectionById` đã trả `undefined`
   * cho đúng trường hợp ấy.
   *
   * Tab Issue dùng lại tham số này cho đúng một giá trị: `client-requests` mở
   * sẵn popup Khách hàng ở mục Chờ duyệt — đích của thông báo "khách xin vào dự
   * án" (APM-CLINV-c).
   */
  section: z.string().optional().catch(undefined),
  /**
   * `documents` và `objectives` bị bỏ ở APM-ROLE-t (về tab Tổng quan), `plan` bị
   * bỏ ở APM-ROLE-v (về nút trong tab Điều phối).
   *
   * `.catch(undefined)` để link cũ **không nổ**: người ta đã chia sẻ và đánh
   * dấu `?tab=documents`, và `validateSearch` ném thì cả trang trắng. Rơi về
   * Tổng quan là đúng đích chứ không phải một mặc định tuỳ tiện — đó chính là
   * nơi tài liệu và mục tiêu đã chuyển tới.
   */
  /**
   * `board` và `gantt` là **tên cũ**: chúng gộp thành tab `tasks` ở
   * `APM-BOARD-b`, và trở thành hai *cách nhìn* của nó.
   *
   * Ánh xạ thay vì để `.catch()` nuốt: link tới board của một sprint là thứ
   * người ta dán cho nhau, và rơi về Tổng quan thì người nhận không hiểu vì
   * sao mình không thấy cái được nhắc tới.
   */
  tab: z
    .string()
    /*
      `backlog` cũng về `tasks` — APM-AI-n.

      Bốn skill khai `manualAlternative` trỏ tới `?tab=backlog`, một tab chưa
      bao giờ tồn tại: backlog nằm **trong** tab Công việc. Không có dòng này
      thì `.catch()` nuốt giá trị và người dùng rơi về tab đầu tiên — đúng kiểu
      hỏng im lặng mà không ai đi báo lỗi.
    */
    .transform((value) =>
      value === 'board' || value === 'gantt' || value === 'backlog' ? 'tasks' : value,
    )
    .pipe(
      z.enum([
        'overview',
        'sprints',
        'tasks',
        'raid',
        'changes',
        'coordination',
        'replay',
        'reports',
        'budget',
        'testing',
        'design',
        'git',
        'issues',
        'settings',
      ]),
    )
    .optional()
    .catch(undefined),
  /**
   * Cách nhìn của tab Công việc — APM-BOARD-b.
   *
   * ⚠️ Tên là `taskView`, **không** phải `view`: `view` đã thuộc về Replay
   * (`ReplayView`) từ APM-902. Dùng chung một tham số nghĩa là đổi cách nhìn ở
   * tab này sẽ đổi luôn chế độ xem của đoạn phim, và link chia sẻ của tab này
   * mang theo trạng thái của tab kia.
   */
  taskView: z.enum(TASK_VIEWS).optional().catch(undefined),
  /**
   * Mốc thời gian của bộ báo cáo (tab `reports`), ISO 8601.
   *
   * Tên khác `at` của Replay có chủ đích: hai tab nói về hai khái niệm khác
   * nhau — `asOf` là "vẽ số liệu tại mốc này", còn `at` là vị trí con trỏ của
   * đoạn phim. Dùng chung một tham số thì kéo thanh tua sẽ đổi luôn mốc của
   * báo cáo, và link chia sẻ của tab này mang theo trạng thái của tab kia.
   */
  asOf: z.string().optional().catch(undefined),
  /**
   * Nhóm biểu đồ của tab Báo cáo.
   *
   * `.transform()` trước `.pipe()` để đổi tên cũ `waterfall` → `schedule`
   * (APM-REPORT-b): báo cáo là thứ người ta **dán link cho nhau**, và nếu tên
   * cũ rơi vào `.catch()` thì link đã chia sẻ vẫn mở được nhưng mở sai nhóm —
   * hỏng im lặng, đúng kiểu không ai đi báo lỗi.
   */
  chart: z
    .string()
    .transform((value) => LEGACY_REPORT_GROUPS[value] ?? value)
    .pipe(z.enum(REPORT_GROUPS))
    .optional()
    .catch(undefined),
  /**
   * Task đang mở drawer — đích của deep-link từ thông báo (APM-NOTI-c).
   *
   * ⚠️ `z.string()` chứ **không** phải một schema JSON hoá được. Router phân
   * tích giá trị search bằng `JSON.parse`, nên bẫy đã ghi cho `play` (khai
   * `z.literal('1')` rồi tham số biến mất im lặng) không áp ở đây: uuid là thứ
   * `JSON.parse` không đọc nổi, nên nó tới đây nguyên vẹn dưới dạng chuỗi.
   */
  taskId: z.string().optional().catch(undefined),
  /**
   * Issue đang mở chi tiết ở tab Issue — APM-ISC-d. Đích của link trong thông báo
   * dành cho đội xử lý; uuid nên `z.string()` được (cùng lý do với `taskId`).
   */
  issueId: z.string().optional().catch(undefined),
  /**
   * Trang Files của dự án — APM-FILES-b. Mở từ khối Tài liệu ở tab Tổng quan.
   *
   * `z.boolean()` chứ không `z.literal('1')`: router `JSON.parse` giá trị, nên
   * `?files=true` tới đây là boolean (cạm bẫy APM-903b).
   */
  files: z.boolean().optional().catch(undefined),
  /**
   * Sổ chốt phương án đang mở — APM-DSHEET-c. Trang con của tab Tổng quan, mở
   * từ khối Sổ chốt phương án ở khu Tài liệu (như `files`).
   *
   * ⚠️ `z.string()`: uuid là thứ `JSON.parse` không đọc nổi nên tới đây nguyên
   * vẹn dưới dạng chuỗi (cùng lý do với `taskId`).
   */
  sheet: z.string().optional().catch(undefined),
  /**
   * Tài liệu mở sẵn xem trước ở khu Tài liệu của tab Tổng quan — thông báo "gửi
   * duyệt tài liệu" dẫn tới đây. Chuỗi: uuid không qua được `JSON.parse`.
   */
  doc: z.string().optional().catch(undefined),
  /**
   * Mốc xem sổ (luật #7) — "Xem sổ lúc này" ở Lịch sử một câu. Tách khỏi
   * `asOf` của tab Báo cáo: tham số giữ qua lúc đổi tab, và mốc của báo cáo
   * không được lặng lẽ biến sổ thành chỉ đọc. ISO datetime không qua được
   * `JSON.parse` nên schema chuỗi là đúng.
   */
  sheetAsOf: z.iso.datetime({ offset: true }).optional().catch(undefined),
  /**
   * Chế độ của trang sổ: Xem / Sửa — APM-DSX-e. Bỏ trống ⇒ theo trạng thái sổ
   * (sổ nháp mở ở Sửa). `z.enum` là đúng: `JSON.parse('view')` hỏng nên giá trị
   * tới đây nguyên là chuỗi.
   */
  sheetMode: z.enum(DECISION_SHEET_MODES).optional().catch(undefined),
  /**
   * Sprint đang xem ở tab Board — APM-SPRINT-b.
   *
   * ⚠️ `z.string()` chứ không phải schema JSON hoá được: router phân tích search
   * bằng `JSON.parse`, và uuid là thứ nó không đọc nổi nên tới đây nguyên vẹn.
   */
  sprintId: z.string().optional().catch(undefined),
  /**
   * Vòng test đang xem ở tab Kiểm thử — APM-TEST-h.
   *
   * Ở URL để vạch mốc trên Gantt bấm được sang **đúng vòng** (§9.3 mức 2), và
   * để "vòng 2 có gì" chia sẻ được thành một đường dẫn. Bỏ trống ⇒ server chọn
   * (`TestingOverview.currentRoundId`: vòng đang mở, hoặc vòng mới nhất).
   *
   * ⚠️ `z.string()` cùng lý do với `sprintId`: uuid là thứ `JSON.parse` không
   * đọc nổi nên nó tới đây nguyên vẹn dưới dạng chuỗi.
   */
  roundId: z.string().optional().catch(undefined),
  /**
   * Lỗi đang mở ở ngăn chi tiết của tab Kiểm thử — đích của bốn thông báo luồng
   * lỗi (APM-TEST2-b) và của "Lỗi cần sửa" ở trang Việc của tôi (APM-TEST2-e).
   * Ngăn chi tiết đọc nó ở APM-TEST2-c.
   *
   * ⚠️ `z.string()` cùng lý do với `sprintId`: uuid là thứ `JSON.parse` không
   * đọc nổi nên nó tới đây nguyên vẹn dưới dạng chuỗi.
   */
  bug: z.string().optional().catch(undefined),
  /**
   * Tab Thiết kế — docs/plan-design-control.md §9. File, bản chụp và màn hình
   * đang mở nằm ở URL để "màn hình Login ở bản chụp hôm qua" chia sẻ được.
   *
   * ⚠️ `z.string()`: uuid và node id Figma (`1:14750`) đều là thứ `JSON.parse`
   * không đọc nổi, nên chúng tới đây nguyên vẹn dưới dạng chuỗi.
   */
  designFile: z.string().optional().catch(undefined),
  designSnapshot: z.string().optional().catch(undefined),
  designScreen: z.string().optional().catch(undefined),
  /** `<base>...<head>` — trang so sánh (§9.2). Hai uuid nối bằng `...`, không phải JSON. */
  designCompare: z.string().optional().catch(undefined),
  /**
   * Tab Git — docs/plan-git-integration.md §8.1. Màn con đang xem và MR đang mở
   * ngăn bên nằm ở URL để "MR !45 của kho này" chia sẻ được.
   *
   * ⚠️ `z.string()`, **không** `z.enum`: danh mục màn con nằm ở
   * `features/git/views.tsx` (task sau thêm màn Bản phát hành bằng một dòng ở đó),
   * và `GitPanel` tự rơi về màn đầu khi id lạ — một enum ở đây là chỗ thứ hai phải
   * nhớ sửa, và quên thì `.catch()` nuốt giá trị, link mở sai màn không lời nào.
   * Id màn (`changeRequests`) và `<uuid>:<số MR>` đều là thứ `JSON.parse` không
   * đọc nổi nên tới đây nguyên vẹn dưới dạng chuỗi.
   */
  gitView: z.string().optional().catch(undefined),
  gitCr: z.string().optional().catch(undefined),
  /** Bản phát hành đang xem (uuid — `JSON.parse` không đọc nổi nên tới đây là chuỗi). APM-GIT-i. */
  gitRelease: z.string().optional().catch(undefined),
  /**
   * Kỳ đang xem của dự án bảo trì — APM-MAINT-e.
   *
   * ⚠️ `z.string()` cùng lý do với `sprintId`: router phân tích search bằng
   * `JSON.parse`, và `2026-Q3` không phải JSON hợp lệ nên tới đây nguyên vẹn.
   * Nhưng `2026` **thì là** — nó tới dưới dạng `number`, nên `periodRange` phải
   * nhận cả hai (nó nhận: `String()` ở chỗ dùng). Đây đúng là cạm bẫy
   * APM-903b đã ghi, chỉ khác chiều.
   */
  period: z.coerce.string().optional().catch(undefined),
  /**
   * Ba tham số của Replay (`APM-902`, spec 17.4).
   *
   * Nằm ở URL vì spec đòi "chia sẻ link kèm mốc thời gian cụ thể để dùng trong
   * họp" — mốc đang xem **là** đường dẫn. `catch()` chứ không ném: một link
   * chép tay bị cắt mất ký tự vẫn phải mở được màn hình, chỉ là về mặc định.
   */
  at: z.iso.datetime().optional().catch(undefined),
  view: ReplayView.optional().catch(undefined),
  compare: z.iso.datetime().optional().catch(undefined),
  /**
   * Chế độ Play (`APM-903`) — link mở ra đúng cảnh đang tua.
   *
   * ⚠️ `z.boolean()` chứ không phải `z.literal('1')`: TanStack Router phân tích
   * giá trị search bằng `JSON.parse`, nên `?play=1` tới đây dưới dạng **number**
   * `1`, không phải chuỗi `'1'`. Schema chuỗi sẽ trượt, `.catch()` nuốt lỗi, và
   * tham số **biến mất im lặng** khỏi URL — mất luôn khả năng chia sẻ đúng cảnh.
   */
  play: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/projects')({
  validateSearch: ProjectSearch,
  component: ProjectsPage,
});

/** Khoá `localStorage` ghi nếp gấp của thanh bên. */
const SIDEBAR_COLLAPSED_KEY = 'apm.sidebar.collapsed';

/**
 * Nút "Tất cả dự án" — áo **nút thật**, không phải áo tab (người dùng yêu
 * cầu 2026-10-07).
 *
 * Bản trước mặc `GLASS_TAB_TRIGGER_CLASS` không khung: chữ xám trên nền trong,
 * nên nó đọc như một dòng tiêu đề của danh sách bên dưới chứ không như thứ bấm
 * được — nhất là trong ngăn "Chọn dự án" ở điện thoại, nơi nó đứng một mình.
 * Nay có nền kính + viền (`btn-glass`, cùng áo với nút outline), icon trong ô
 * màu chính, và `›` ở cuối nói "bấm vào là sang một màn khác".
 *
 * Đang mở màn sắp xếp (`aria-pressed`) thì nhuốm màu chính — trạng thái bật
 * thấy được cả khi không đọc chữ.
 */
const PROJECT_LIST_BUTTON_CLASS = cn(
  'btn-glass group/plist inline-flex h-9 min-w-0 items-center gap-2 rounded-full pl-1.5 pr-2.5 text-sm font-medium text-foreground',
  'transition-[scale,background-color] duration-150 active:scale-[0.97] motion-reduce:active:scale-100',
  'aria-pressed:bg-primary/12 aria-pressed:text-primary',
);

/** Ô tròn chứa icon — đậm màu chính khi đang bật. */
const PROJECT_LIST_ICON_CLASS =
  'flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary group-aria-pressed/plist:bg-primary group-aria-pressed/plist:text-primary-foreground';

/**
 * Thanh bên có đang thu gọn — đọc một lần lúc dựng.
 *
 * `try/catch` vì `localStorage` **ném** ở chế độ ẩn danh của một số trình
 * duyệt; mặc định là mở rộng, tức trạng thái mà mọi thông tin còn đọc được.
 */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Đích của cú kéo = thứ **nằm dưới con trỏ** — APM-PSET-c.
 *
 * ## Vì sao không dùng mặc định (`rectIntersection`)
 *
 * Mặc định chọn vùng thả có **diện tích giao** lớn nhất với hình chữ nhật đang
 * kéo. Thứ đang kéo ở đây là một **dòng bảng** rộng gần cả màn hình, còn thanh
 * bên rộng 240px — nên diện tích giao với các dòng bảng khác luôn lớn hơn, và
 * cú kéo sang thanh bên không bao giờ tới được thanh bên.
 *
 * Đo trên browser 2026-09-18: kéo dòng DWH trong bảng tới hàng SUP trên thanh
 * bên, `over.id` là `dir:<id của SUP>` — tức **hàng bảng** SUP, không phải hàng
 * thanh bên. Thứ tự thanh bên không đổi, và không gì nói ra vì sao.
 *
 * (Trước khi dòng bảng thành vùng thả, cùng phép mặc định ấy chọn cái `<ul>`
 * của cả danh sách — `beforeId` thành `null` và dự án rơi xuống **cuối** thanh
 * bên bất kể người dùng trỏ vào hàng nào.)
 *
 * `pointerWithin` xét vùng thả **chứa con trỏ** rồi xếp theo khoảng cách tới
 * tâm: hàng dưới con trỏ thắng, và cái `<ul>` chỉ thắng khi con trỏ ở vùng
 * trống bên dưới hàng cuối — đúng ý nghĩa của hai vùng thả ấy.
 *
 * `rectIntersection` là đường dự phòng: `pointerWithin` trả rỗng khi không có
 * toạ độ con trỏ, tức khi kéo bằng **bàn phím** (`KeyboardSensor`).
 */
function dropUnderPointer(args: Parameters<typeof pointerWithin>[0]): Collision[] {
  const byPointer = pointerWithin(args);
  return byPointer.length > 0 ? byPointer : rectIntersection(args);
}

function ProjectsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const projects = useQuery({ queryKey: boardKeys.projects(), queryFn: boardApi.projects });
  /** Không gian cá nhân — APM-PSPACE-b: ba tab không tồn tại + một dòng giải thích. */
  const orgIsPersonal = useOrgIsPersonal();
  /** Cộng tác viên ở tổ chức đang mở ⇒ nhãn "Cộng tác viên" trên mọi dòng của màn Danh sách dự án. */
  const asGuest = useOptionalUser()?.orgRole === 'GUEST';
  const limitedShell = useShellMode() === 'limited';
  const [fallbackId, setFallbackId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  /**
   * Quyền để ẩn/hiện tab Báo cáo. Cùng `queryKey` với layout `_app` nên React
   * Query trả từ cache — không thêm một lời gọi mạng nào.
   */
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });
  /**
   * "Dự án mới" only with `project:create` (org-level grant) — a `GUEST` has none, and the
   * button opened a dialog whose submit could only answer 403. Hidden while abilities load:
   * a button that appears late beats one that vanishes.
   */
  const canCreateProject = abilities.data?.global.includes('project:create') ?? false;

  /**
   * Danh bạ dự án + danh sách sidebar người này đã sắp — APM-PSET-c.
   *
   * ⚠️ Mọi hook ở đây phải đứng **trước** các nhánh `return` sớm bên dưới. Gọi
   * `useQuery` sau một `return` có điều kiện là gọi hook có điều kiện, và React
   * đổ cả trang với "Rendered more hooks than during the previous render" —
   * lỗi đã xảy ra thật khi làm badge Cấu hình.
   */
  const queryClient = useQueryClient();

  const directory = useQuery({
    queryKey: projectKeys.directory(),
    queryFn: projectApi.directory,
    retry: false,
  });

  /*
    Thanh bên thu gọn — nhớ giữa các lần mở trang.

    `localStorage` chứ không phải server: đây là trạng thái giao diện của **một
    thiết bị**, cùng loại với cờ "đã xem hướng dẫn" (`tour-steps.ts`). Bọc
    `try/catch` vì `localStorage` **ném** ở chế độ ẩn danh của một số trình
    duyệt, và một thanh bên không nhớ được nếp gấp thì không được làm sập trang.
  */
  const [collapsed, setCollapsed] = useState(readCollapsed);
  /**
   * Thanh bên bị **ép** gấp vì dải tab đã hết chỗ nhường — APM-PSET-g.
   *
   * Tách khỏi `collapsed` (nếp gấp người dùng tự chọn, nhớ trong `localStorage`)
   * chứ không ghi đè nó: trả lại màn hình rộng thì thanh bên phải về đúng trạng
   * thái người dùng để nó, không phải trạng thái mà một lần cửa sổ hẹp áp lên.
   */
  const [sidebarSqueezed, setSidebarSqueezed] = useState(false);
  /** Hộp sắp lại thanh tab, mở từ nút bút chì ở cuối dải tab — APM-PSET-i. */
  const [editingTabs, setEditingTabs] = useState(false);

  /** Gấp vì người dùng muốn, **hoặc** vì hàng tab không còn chỗ. */
  const sidebarCollapsed = collapsed || sidebarSqueezed;

  /*
    Điện thoại dựng đứng: thanh bên **không** đứng thường trực nữa mà trượt ra
    từ nút tên dự án (`ProjectDrawer`) — người dùng báo trên iPhone, 2026-09-30.
    Gấp còn 48px thì cột ấy + khe + viền vẫn ăn ~64px của 390px ở **mọi** tab,
    trong khi người ta chỉ cần nó lúc đổi dự án.
  */
  const narrow = useNarrowScreen();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = (): void => {
    // Bấm tay là đè lên cả cú gấp tự động: người dùng vừa nói họ muốn gì.
    setSidebarSqueezed(false);
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Không lưu được thì thôi: lần sau mở lại ở trạng thái mặc định.
      }
      return next;
    });
  };

  const [listEditing, setListEditing] = useState(false);
  /** Bản nháp: tập đã ẩn và thứ tự. `null` = chưa sửa gì, đọc theo server. */
  const [draftHidden, setDraftHidden] = useState<ReadonlySet<string> | null>(null);
  const [draftOrder, setDraftOrder] = useState<readonly string[] | null>(null);
  /**
   * Thứ tự của **bảng** — mảng riêng, không dùng chung với thanh bên.
   *
   * Kéo một dòng trong bảng chỉ sắp lại bảng; thanh bên có thứ tự của nó (yêu
   * cầu người dùng 2026-09-18). Dùng chung một mảng thì mỗi cú kéo trong bảng
   * âm thầm dời chỗ dự án ấy trên thanh bên.
   */
  const [draftTableOrder, setDraftTableOrder] = useState<readonly string[] | null>(null);

  // Luật "có trên sidebar" dùng chung với trang Tổng quan — APM-OVW-a.
  const savedHidden = savedHiddenIds(directory.data);
  const hiddenNow = draftHidden ?? savedHidden;
  const orderNow = draftOrder ?? directory.data?.order ?? [];
  const tableOrderNow = draftTableOrder ?? directory.data?.tableOrder ?? [];

  /*
    Ngưỡng 6px giống board: không có nó thì mỗi cú bấm vào tay cầm đều bị hiểu
    là bắt đầu kéo, và nút thùng rác ngay cạnh không bấm được.
  */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const saveSidebar = useMutation({
    mutationFn: (layout: {
      hidden: readonly string[];
      order: readonly string[];
      tableOrder: readonly string[];
    }) =>
      projectApi.setSidebar({
        hidden: [...layout.hidden],
        order: [...layout.order],
        tableOrder: [...layout.tableOrder],
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectKeys.directory() });
      setDraftHidden(null);
      setDraftOrder(null);
      setDraftTableOrder(null);
      setInsertBefore(null);
      setListEditing(false);
      toast.success(t('projectList.saved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  /** Bản xem trước của một cú kéo **từ bảng** — chỗ trống, chưa thêm thật. */
  const [insertBefore, setInsertBefore] = useState<{
    projectId: string;
    beforeId: string | null;
  } | null>(null);

  /**
   * Thoát màn hình mà **không** lưu — dùng cho cả nút Huỷ, nút "Danh sách dự
   * án" bấm lần thứ hai, và cú bấm vào tên dự án.
   *
   * Một hàm cho ba lối ra: ba bản sao sẽ lệch nhau ở lần ai đó thêm một mảnh
   * state vào bản nháp, và lối ra bị quên sẽ mang state cũ sang lượt sau.
   */
  const leaveList = (): void => {
    setDraftHidden(null);
    setDraftOrder(null);
    setDraftTableOrder(null);
    setInsertBefore(null);
    setListEditing(false);
  };

  const setHidden = (projectId: string, hidden: boolean): void => {
    setDraftHidden((current) => {
      const next = new Set(current ?? savedHidden);
      if (hidden) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
  };

  /**
   * Dời (hoặc chèn) một dự án trong thanh bên — dùng cho **cả** `onDragOver` và
   * `onDragEnd`.
   *
   * Một phép tính cho hai thời điểm: bản xem trước **chính là** nước đi sẽ
   * chốt. Hai phép tính khác nhau là cách chắc chắn nhất để thả ra một chỗ khác
   * chỗ vừa nhìn thấy — xem chú thích cùng nội dung ở `board.tsx`.
   */
  const previewMove = (activeId: string, overId: string | null, commit: boolean): void => {
    const projectId = projectIdOfDrag(activeId);
    const beforeId = overId === null || overId === SIDEBAR_DROP_ID ? null : overId;
    if (beforeId === projectId) return;

    const fromTable = activeId.startsWith(DIR_DRAG_PREFIX) && hiddenNow.has(projectId);

    /*
      Kéo **từ bảng** sang: trong lúc kéo chỉ hiện một **chỗ trống**.

      Bản trước bỏ ẩn ngay trong `onDragOver`, nên dự án hiện ra thật khi con trỏ
      mới lướt qua — và lướt ra là nó biến mất. Mỗi lần đi ngang thanh bên là một
      hàng nhấp nháy. Nay nó chỉ được thêm ở `onDragEnd` (`commit`).
    */
    if (fromTable && !commit) {
      setInsertBefore((current) =>
        current && current.projectId === projectId && current.beforeId === beforeId
          ? current
          : { projectId, beforeId },
      );
      return;
    }

    if (fromTable) setHidden(projectId, false);

    setDraftOrder((current) => {
      /*
        `projects.data` có thể chưa về: hàm này khai **trên** các nhánh return
        sớm (hook phải đứng trước chúng), nên không được giả định đã có dữ liệu.
      */
      const base =
        current ??
        initialOrder(
          visibleOnSidebar(projects.data ?? [], hiddenNow).map((project) => project.id),
          directory.data?.order ?? [],
        );
      /*
        Hai phép dời cho hai việc khác nhau:

        - **Chèn** (`fromTable`, hoặc thả vào chỗ trống ⇒ `beforeId === null`):
          `moveBefore`. Dự án chưa có trong danh sách nên không có "chiều kéo"
          nào để đọc.
        - **Đổi chỗ** một hàng đã ở trên thanh bên: `moveToward`. `moveBefore`
          ở đây chèn vào *trước* hàng đang trỏ, nên kéo xuống một bậc là một
          nước đi **không đổi gì** — thả DWH lên ô thứ hai khi nó đang ở ô thứ
          nhất và danh sách y nguyên (đo được trên browser 2026-09-18, cùng bệnh
          với bảng).

        `moveToward` không idempotent, và điều đó an toàn ở đây vì nước đi tự ổn
        định: sau khi đổi chỗ, hàng nằm dưới con trỏ **là** hàng đang kéo, nên
        nhịp `onDragOver` kế tiếp gặp `beforeId === projectId` và dừng ngay ở
        dòng đầu hàm.
      */
      // `moveBefore` trả về **chính** mảng cũ khi không đổi gì, nên `setState`
      // này không gây vẽ lại — `onDragOver` bắn theo từng pixel.
      return fromTable || beforeId === null
        ? moveBefore(base, projectId, beforeId)
        : moveToward(base, projectId, beforeId);
    });
  };

  /*
    `?projectId` trỏ vào một dự án **không còn** trong danh sách người này thấy
    được — đã xoá, demo vừa dựng lại, hay đã rời dự án — APM-NOTIGONE-c.

    Trước đây trang lặng lẽ mở dự án đầu tiên **nhưng giữ nguyên phần còn lại
    của URL** (`tab`, `bug`, `taskId`…): thông báo RAID mở RAID của một dự án
    khác, thông báo lỗi gọi lỗi ấy dưới dự án khác ⇒ 404 "Không tìm thấy nội
    dung". Link cũ, dấu trang, link dán trong chat đều đi qua đây. Nói ra rồi
    về trang Dự án sạch tham số; trong lúc chuyển không vẽ tab nào, để không
    một panel nào kịp gọi API với dự án sai.
  */
  const goneProjectId =
    projects.data && search.projectId && !projects.data.some((p) => p.id === search.projectId)
      ? search.projectId
      : null;
  /*
    Link xem trước tài liệu (`?doc=`, APM-PVLINK) tới dự án không có ở đây: có thể nó
    ở **tổ chức khác** mà người xem là thành viên — thì chuyển tổ chức rồi mở đúng
    link ấy, như `/work?task=` (APM-PSPACE-j). Không thì câu chung "không tìm thấy
    hoặc không có quyền" — không nói dự án có tồn tại hay không.
  */
  const linkedDoc = goneProjectId ? search.doc : undefined;
  const elsewhere = useQuery({
    queryKey: projectKeys.elsewhere(),
    queryFn: projectApi.elsewhere,
    enabled: linkedDoc !== undefined,
  });
  const leftForGone = useRef<string | null>(null);
  useEffect(() => {
    if (!goneProjectId) {
      leftForGone.current = null;
      return;
    }
    if (leftForGone.current === goneProjectId) return;
    if (linkedDoc !== undefined) {
      if (elsewhere.isPending) return;
      leftForGone.current = goneProjectId;
      const target = elsewhere.data?.projects.find((project) => project.id === goneProjectId);
      const unavailable = (): void => {
        toast.error(t('myFiles.preview.linkUnavailable'), { id: 'project-gone' });
        void navigate({ search: {}, replace: true });
      };
      if (target) {
        const href = `${window.location.pathname}${window.location.search}`;
        switchOrgAndGo(target.orgId, href, { replace: true }).catch(unavailable);
      } else unavailable();
      return;
    }
    leftForGone.current = goneProjectId;
    // `id`: một lần dù effect chạy hai lần (StrictMode ở dev) hay link cũ được mở liền tay.
    toast.error(t('projects.goneToast'), { id: 'project-gone' });
    void navigate({ search: {}, replace: true });
  }, [goneProjectId, linkedDoc, elsewhere.isPending, elsewhere.data, navigate, t]);

  if (projects.isPending) return <Skeleton className="h-64 w-full" />;
  if (projects.isError) {
    return (
      <ErrorState
        message={errorMessage(projects.error, t)}
        onRetry={() => void projects.refetch()}
      />
    );
  }

  /**
   * Dự án vừa tạo từ nguồn đồng bộ ⇒ mở thẳng tab Điều lệ, nơi khối Đồng bộ
   * hiện "Đang nhập lần đầu" (APM-SYNC-d, plan-project-sync §7.1 bước 4).
   */
  const openCreatedProject = (projectId: string) => {
    void navigate({ search: { projectId, tab: 'overview' } });
  };

  const pick = (projectId: string) => {
    setFallbackId(projectId);
    /**
     * Đổi dự án thì **bỏ** sprint và task đang mở — APM-BOARD-c.
     *
     * Cả hai là id của dự án cũ. Giữ lại thì tab Công việc mở ra với một sprint
     * không thuộc dự án đang xem: bộ chọn hiện một tên, khung hiện một tên
     * khác, và không gì nói cho người dùng biết vì sao.
     */
    void navigate({ search: { ...search, projectId, sprintId: undefined, taskId: undefined } });
  };

  /**
   * Chưa có dự án nào — nhưng **vẫn giữ sidebar**, vì nút tạo sống ở đó.
   *
   * Bản trước trả về mỗi `EmptyState` nói "Tạo dự án đầu tiên để bắt đầu" mà
   * không có nút nào trên màn hình: một lời chỉ dẫn tới một chỗ vừa bị ẩn. Và
   * đây là màn hình **đầu tiên** của mọi tài khoản mới, nên nó là ấn tượng đầu
   * tiên rằng ứng dụng hỏng.
   */
  if (projects.data.length === 0) {
    return (
      <div className="flex min-h-0 gap-4">
        {/*
          `data-tour` — neo dự phòng của tour AI (APM-TOUR-ai): chưa có dự án
          thì mọi nút AI trong dự án đều chưa có, và chỗ đúng để chỉ vào là nơi
          tạo dự án đầu tiên.
        */}
        <div
          data-tour="project-sidebar"
          className="flex w-60 shrink-0 flex-col gap-2 self-stretch border-r border-border pr-3"
        >
          {/*
            Không có "Tất cả dự án" ở nhánh này: chưa có dự án nào thì không có danh
            sách nào để sắp. Đường tạo dự án đầu tiên nằm **trong** thanh bên,
            ngay dưới dòng "Dự án · 0" — xem `ProjectSidebar.onCreate`.
          */}
          {/* `''` không khớp id nào — chưa có dự án thì không có gì để tô sáng. */}
          <ProjectSidebar
            projects={[]}
            selectedId=""
            onSelect={pick}
            {...(canCreateProject ? { onCreate: () => setCreating(true) } : {})}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/*
            Nền **luôn** được vẽ, hộp thoại nằm đè lên — APM-ROLE-z.

            Trước đây `CreateProjectDialog` là một khối chèn vào trang nên nó
            thay chỗ nội dung. Nay nó là lớp phủ: giữ nhánh "hoặc/hoặc" thì mở
            hộp ra là phía sau trắng trơn, và đóng lại thì nội dung "nhảy" về —
            người dùng đọc thành một cú giật, không phải một lớp phủ.
          */}
          {/*
            Không đặt nút tạo ở đây nữa: nó đã nằm trong thanh bên, ngay dưới
            dòng "Dự án". Hai nút giống nhau cách nhau 30cm trên cùng một màn
            hình trống là nhiễu, không phải hai đường đi.
          */}
          {/* Không gian cá nhân — APM-PSPACE-e: đơn chờ, kiểm tra lời mời, tự lập tổ chức. */}
          <PersonalSpaceCard />
          <EmptyState title={t('board.noProject')} description={t('board.noProjectHelp')} />
          {creating ? (
            <CreateProjectDialog
              onDone={() => setCreating(false)}
              onOpenProject={openCreatedProject}
            />
          ) : null}
        </div>
      </div>
    );
  }

  /**
   * Mặc định mở dự án **đầu tiên còn hiện ở thanh bên**, không phải dự án mới
   * nhất.
   *
   * `projects.data` xếp theo `createdAt desc`, nên `[0]` có thể là một dự án
   * người dùng đã ẩn khỏi mục truy cập nhanh — tức vào `/projects` là rơi vào
   * đúng cái mình vừa dọn đi, và thanh bên không có hàng nào được tô sáng.
   *
   * `?projectId` và dự án vừa bấm vẫn thắng: ẩn khỏi thanh bên không phải là
   * chặn đường vào.
   */
  if (goneProjectId) return <Skeleton className="h-64 w-full" />;

  const preferredId = orderRows(visibleOnSidebar(projects.data, hiddenNow), orderNow)[0]?.id;

  const selectedId = search.projectId ?? fallbackId ?? preferredId ?? projects.data[0]!.id;
  const selected = projects.data.find((project) => project.id === selectedId) ?? projects.data[0]!;
  /**
   * Quyền đọc báo cáo xét **theo dự án đang chọn**, không chỉ theo quyền toàn
   * tổ chức.
   *
   * Màn hình báo cáo cũ nằm ở thanh điều hướng nên nó chỉ xét được quyền toàn
   * tổ chức — và một PM chỉ có quyền ở dự án của mình sẽ **không thấy** mục
   * đó. Là tab của dự án thì câu hỏi đúng hơn: người này có đọc được báo cáo
   * của *dự án này* không.
   */
  /**
   * Quyền hiệu lực **ở dự án đang chọn** — APM-PSET-a.
   *
   * Gộp quyền toàn tổ chức với quyền riêng của dự án, đúng phép hợp mà bốn cờ
   * bên dưới đang tự làm lấy từng cái một. Danh mục mục Cấu hình lọc trên danh
   * sách này thay vì trên một cờ boolean, để mỗi mục khai đúng quyền mà API
   * phía sau nó đòi.
   */
  const projectPermissions: readonly Permission[] =
    abilities.data === undefined
      ? []
      : [
          ...abilities.data.global,
          ...(abilities.data.perProject.find((entry) => entry.projectId === selected.id)
            ?.permissions ?? []),
        ];

  /**
   * `task:create` — nút "Tạo việc sửa lỗi" ở tab Kiểm thử (§9.2).
   *
   * Quyền riêng, không mượn `canExecuteTests`: route ấy tạo ra một task thật,
   * thứ sẽ lên Gantt và vào burndown. Ai ghi được lỗi không đương nhiên được
   * thêm việc vào kế hoạch dự án.
   */
  const canCreateTask =
    abilities.data !== undefined &&
    (abilities.data.global.includes('task:create') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('task:create') ??
        false));

  /** `task:update` ở dự án đang chọn — mở nối phụ thuộc trên Gantt. */
  const canEditTasks =
    abilities.data !== undefined &&
    (abilities.data.global.includes('task:update') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('task:update') ??
        false));

  /** `project:update` ở dự án đang chọn — mở phần thêm/sửa cột board và link mời. */
  const canEditColumns =
    abilities.data !== undefined &&
    (abilities.data.global.includes('project:update') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('project:update') ??
        false));

  /** `workflow:manage` ở dự án đang chọn — menu ••• và nút thêm cột trên board. */
  const canManageColumns =
    abilities.data !== undefined &&
    (abilities.data.global.includes('workflow:manage') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('workflow:manage') ??
        false));

  /**
   * `task:assign` ở dự án đang chọn — mở ô "Người làm" trong form việc.
   *
   * Quyền riêng, không suy từ `canEditTasks`: CONTRIBUTOR sửa được việc của
   * mình nhưng **không** giao việc cho ai, nên gộp hai thứ sẽ hiện một ô mà
   * `PATCH /tasks/:id/assignee` luôn trả 403.
   */
  const canAssign =
    abilities.data !== undefined &&
    (abilities.data.global.includes('task:assign') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('task:assign') ??
        false));

  /** `ai_suggestion:approve` — duyệt phương án AI đề xuất (APM-1313). */
  const canApproveSuggestions =
    abilities.data !== undefined &&
    (abilities.data.global.includes('ai_suggestion:approve') ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === selected.id)
        ?.permissions.includes('ai_suggestion:approve') ??
        false));

  /**
   * Kỳ hiệu lực của dự án bảo trì — APM-MAINT-e.
   *
   * Tính **một lần ở đây** rồi đưa cho cả tab Công việc lẫn tab Báo cáo. Trước
   * đó mỗi bên tự rơi về mặc định của mình: Công việc lấy tháng này, Báo cáo
   * lấy cửa sổ 90 ngày — nên cùng một màn hình, cùng một dự án, hai tab nói về
   * hai khoảng thời gian khác nhau và không gì nói ra điều đó.
   *
   * `String()` vì router phân tích search bằng `JSON.parse`: `?period=2026` tới
   * đây dưới dạng number (cạm bẫy APM-903b).
   */
  const activePeriod =
    selected.methodology !== 'MAINTAIN'
      ? null
      : periodRange(String(search.period ?? ''))
        ? String(search.period)
        : periodOf(new Date(), 'month');

  const canReadReports =
    abilities.data === undefined ||
    abilities.data.global.includes('report:read_project') ||
    (abilities.data.perProject
      .find((entry) => entry.projectId === selected.id)
      ?.permissions.includes('report:read_project') ??
      false);

  /*
    Ngân sách — APM-AI-n. Mặc định **ẩn** khi chưa biết quyền, ngược với báo cáo.

    Báo cáo hiện khi `abilities` chưa tải xong vì mọi thành viên dự án đều có
    quyền ấy, nên giấu nó là nháy một tab mất rồi hiện lại. Ngân sách thì ít
    người có: hiện rồi ẩn đi là chỉ cho người ta một tab họ không mở được.
  */
  const budgetPermission = (
    permission:
      | 'budget:read'
      | 'budget:manage'
      | 'test:read'
      | 'test:execute'
      | 'test:manage'
      | 'issue:read'
      | 'design:read'
      | 'design:write'
      | 'git:read',
  ): boolean =>
    abilities.data?.global.includes(permission) === true ||
    (abilities.data?.perProject
      .find((entry) => entry.projectId === selected.id)
      ?.permissions.includes(permission) ??
      false);
  /*
    Vai trò của người đang xem trong từng dự án, cho sidebar.

    Dựng từ `abilities` đã tải sẵn thay vì gọi thêm endpoint: cùng một nguồn với
    các cửa quyền ngay dưới, nên nhãn trên sidebar không bao giờ nói khác thứ
    giao diện cho phép làm.
  */
  const rolesByProject = new Map(
    (abilities.data?.perProject ?? []).map((entry) => [entry.projectId, entry.role]),
  );
  const canReadBudget = budgetPermission('budget:read');
  const canManageBudget = budgetPermission('budget:manage');
  /** `test:read` — mở cho cả ba vai trong dự án, docs/plan-testing-tab.md. */
  const canReadTesting = budgetPermission('test:read');
  /** `test:execute` — ghi kết quả chạy case và **kết quả test lại**. */
  const canExecuteTests = budgetPermission('test:execute');
  /** `test:manage` — sửa bộ case, môi trường, mở/đóng vòng. */
  const canManageTests = budgetPermission('test:manage');
  /** Tab Thiết kế — `design:read` cho cả ba vai; `design:write` gắn file, chụp, sửa mô tả. */
  const canReadDesign = budgetPermission('design:read');
  const canReadIssues = budgetPermission('issue:read');
  const canWriteDesign = budgetPermission('design:write');
  /** Tab Git — `git:read` cho ba vai trong dự án (không khách, không khách hàng). `GitPanel` tự hỏi `git:act` / `git:manage`. */
  const canReadGit = budgetPermission('git:read');
  /**
   * `budget:manage` KHÔNG ĐIỀU KIỆN — chỉ đúng với người sửa được đơn giá cấp
   * tổ chức (hôm nay là `ORG_ADMIN`).
   *
   * `canManageBudget` ở trên giờ cũng đúng cho một PM quản lý dự án của chính
   * mình (permission-matrix.ts), nhưng route đơn giá vẫn của riêng org-wide —
   * xem `budget.controller.ts`. Đọc thẳng `global` thay vì gọi lại
   * `budgetPermission`: quyền này không có phiên bản theo-dự-án nào để hợp.
   */
  const canManageCostRates = abilities.data?.global.includes('budget:manage') === true;

  /*
    Danh sách tab lấy từ danh mục chung — APM-TAB-a.

    Hai mảng cắm cứng trước đây (bảy tab dự án, hai tab tiện ích) đã chuyển sang
    `features/projects/tab-registry`, mang theo cả hai luật cứng: tab Báo cáo
    hỏi quyền, tab Sprint chỉ có nghĩa với dự án Agile. Chỗ này giờ chỉ còn việc
    cung cấp ngữ cảnh để trả lời chúng.
  */
  const tabContext = {
    methodology: selected.methodology,
    canReadReports,
    canReadBudget,
    canReadTesting,
    canReadDesign,
    canReadIssues,
    canReadGit,
    orgIsPersonal,
  };
  // `null` = PM chưa đụng tới, và danh mục hiểu đúng nghĩa đó — APM-TAB-b.
  const tabs = visibleProjectTabs(tabContext, selected.tabLayout);

  /**
   * Tab mặc định là tab **đầu tiên đang hiện**, không phải một id cắm cứng —
   * APM-TAB-c.
   *
   * Trước đây đây là `'overview'` cứng, với lý lẽ đúng của APM-ROLE-r: mở một
   * dự án lạ mà rơi thẳng vào Board là thấy một đống thẻ trước khi biết dự án
   * này làm gì. Lý lẽ ấy vẫn đúng, và Điều lệ **vẫn** đứng đầu ở thứ tự mặc
   * định — nên hành vi không đổi với dự án chưa ai cấu hình.
   *
   * Cái nó sửa là lúc PM đã sắp lại: kéo Nhân sự lên đầu rồi mở dự án vẫn rơi
   * vào Điều lệ thì thao tác vừa rồi trông như không có tác dụng, và không gì
   * nói ra vì sao.
   *
   * Link cũ trỏ vào `?tab=board` vẫn chạy y nguyên: đây chỉ đổi giá trị khi
   * **không** có tham số.
   *
   * Ngoại lệ — APM-PSPACE-b: link trỏ vào một tab không có ở không gian cá nhân (Nhân sự / Thay
   * đổi / Thiết kế) rơi về tab đầu, thay vì mở một panel chỉ để hiện lỗi 403.
   *
   * Vỏ hạn chế (APM-PSPACE-g): link trỏ vào tab mà vai không có (`isProjectTabAvailable`) cũng
   * rơi về tab đầu — và chờ `abilities` trước khi tin link, vì lúc chưa có quyền thì chưa biết.
   */
  const tabUnavailable =
    search.tab !== undefined &&
    limitedShell &&
    (abilities.data === undefined || !isProjectTabAvailable(search.tab, tabContext));
  const tab =
    (search.tab && !(orgIsPersonal && isBlockedInPersonalSpace(search.tab)) && !tabUnavailable
      ? search.tab
      : undefined) ??
    tabs[0] ??
    'overview';

  const utilityTabs = UTILITY_TAB_IDS;

  /*
    Sidebar = mọi dự án, trừ những cái người này đã ẩn — APM-PSET-c.

    ⚠️ **Không** có ngoại lệ cho dự án đang xem. Bản trước giữ nó lại kể cả khi
    nó bị ẩn, với hai lý lẽ: link chia sẻ trỏ vào dự án đã ẩn vẫn phải mở được,
    và một thanh bên không có hàng nào được tô sáng đọc như đang hỏng.

    Lý lẽ thứ nhất **không cần** ngoại lệ này: `selected` đọc từ `projects.data`
    (mọi dự án người này xem được), không từ `sidebarProjects` — nên một link
    trỏ vào dự án đã ẩn vẫn mở đúng dự án ấy, chỉ là nó không có hàng ở mục truy
    cập nhanh. Lý lẽ thứ hai rẻ hơn cái giá phải trả: nhãn tab đầu tiên đã ghi
    mã và tên dự án đang mở (`ProjectTabLabel`).

    Cái giá ấy là một lỗi người dùng báo **hai lần** (2026-09-18): bấm thùng rác
    trên hàng của dự án đang xem, cột trong bảng đổi thành "Đã ẩn", bấm Hoàn
    thành — và hàng vẫn nằm nguyên đó. Trong cơ sở dữ liệu nó **đã** được lưu là
    ẩn; chỉ có màn hình nói ngược lại, nên nó đọc thành "bấm lưu mà không lưu".

    Lọc tại chỗ dùng chứ không lọc ở server: `GET /projects` là danh sách chung
    của nhiều màn hình, còn "ẩn khỏi sidebar" chỉ là chuyện của sidebar.
  */
  const sidebarProjects = orderRows(visibleOnSidebar(projects.data, hiddenNow), orderNow);

  /**
   * Thứ tự thanh bên **chỉ** đổi khi cú kéo được thả **lên thanh bên** —
   * APM-PSET-c.
   *
   * Bản trước gọi thẳng `previewMove` với `over` là gì cũng được. Thả ra ngoài
   * mọi vùng thả thì `over` là `null`, `beforeId` thành `null`, và dự án bị dồn
   * xuống **cuối** thanh bên — tức kéo một dòng trong bảng rồi nhả tay giữa
   * bảng cũng âm thầm sắp lại thanh bên. Không lỗi, không dấu hiệu, và người
   * dùng chỉ thấy thanh bên tự đổi thứ tự.
   *
   * Cửa đặt ở đây chứ không trong `previewMove` vì đây là chỗ duy nhất biết
   * **thanh bên đang hiện những hàng nào** (`sidebarProjects`).
   */
  const sidebarIds = new Set(sidebarProjects.map((project) => project.id));

  /**
   * Sắp lại **bảng** — chỉ chạy lúc thả.
   *
   * Thứ tự bảng là một mảng riêng: kéo ở đây không dời chỗ dự án ấy trên thanh
   * bên. Bản xem trước trong lúc kéo do `SortableContext` vẽ bằng `transform`,
   * nên không cần (và không được) gọi hàm này trong `onDragOver` — `moveToward`
   * đọc chiều kéo nên gọi lặp lại là hai dòng nhảy qua lại.
   */
  const commitTableMove = (activeId: string, overId: string): void => {
    const rows = directory.data?.rows ?? [];
    const id = projectIdOfDrag(activeId);
    const overProjectId = projectIdOfDrag(overId);
    if (id === overProjectId) return;

    /*
      Cùng nhóm mới dời được: bảng chia nhóm theo **trạng thái dự án**, nên thả
      sang nhóm khác là một nước đi không vẽ ra được — dòng sẽ bật về nhóm cũ
      ngay lúc vẽ lại, và người dùng thấy cú kéo của mình không có tác dụng.
    */
    const groupOf = (projectId: string): string | null => {
      const row = rows.find((entry) => entry.id === projectId);
      return row ? directoryGroupOf(row) : null;
    };
    const group = groupOf(id);
    if (group === null || group !== groupOf(overProjectId)) return;

    // Cùng phép tính với lúc vẽ bảng, nên thứ tự chốt đúng thứ tự vừa thấy.
    setDraftTableOrder(moveToward(directorySequence(rows, tableOrderNow), id, overProjectId));
  };

  const onDrag = (
    event: { active: { id: string | number }; over: { id: string | number } | null },
    commit: boolean,
  ): void => {
    const overId = event.over ? String(event.over.id) : null;
    if (overId === null) return;
    const activeId = String(event.active.id);

    /*
      Đích là một dòng **bảng** ⇒ chuyện của bảng, thanh bên không đụng tới.

      Chỉ nhận khi nguồn cũng là một dòng bảng: kéo một hàng thanh bên ngang qua
      bảng không được sắp lại bảng — hàng ấy nói về mục truy cập nhanh.
    */
    if (overId.startsWith(DIR_DRAG_PREFIX)) {
      if (!activeId.startsWith(DIR_DRAG_PREFIX)) return;
      // Xoá chỗ trống còn sót của một nhịp vừa lướt qua thanh bên.
      if (insertBefore !== null) setInsertBefore(null);
      if (commit) commitTableMove(activeId, overId);
      return;
    }

    // Đích hợp lệ của thanh bên: vùng trống, hoặc một hàng đang ở trên đó.
    if (overId !== SIDEBAR_DROP_ID && !sidebarIds.has(overId)) return;

    previewMove(activeId, overId, commit);
  };

  const layout = (
    <div className="flex min-h-0 gap-4">
      {/*
        Màn hẹp: không có cột này, kể cả lúc sắp "Tất cả dự án" — màn ấy có bản
        riêng cho điện thoại (`ProjectDirectoryCompact`), thanh bên nằm trong thẻ
        "Truy cập nhanh" của nó. Cột 208px cạnh một bảng mười cột ở 390px chỉ còn
        ~140px cho bảng (APM-MBAR-f).
      */}
      {narrow ? null : (
        /*
        Thu gọn = **1/5** bề rộng: `w-60` (240px) → `w-12` (48px).

        Viết sẵn hai lớp thay vì `w-[${…}]` ghép lúc chạy: Tailwind quét **mã
        nguồn** để biết sinh lớp nào, nên một lớp ghép lúc chạy không tồn tại và
        cột sẽ mất bề rộng hoàn toàn — cạm bẫy đã ghi ở CLAUDE.md.
      */
        <div
          className={cn(
            'shrink-0 self-stretch border-r border-border',
            /*
            Mở rộng: 208px khi vùng nội dung còn chật, 240px khi đã rộng.

            240px cố định là gần **hai phần ba** một màn 390px, nên phần còn lại
            không đủ cho một cái bảng. Con số này đo theo container của `main`
            (xem `app-shell.tsx`), tức theo chỗ thật sự có, không theo cửa sổ.
          */
            /*
            Thu gọn: `pr-1` thay `pr-3` — người dùng báo 2026-09-26: ba lớp
            padding lồng nhau (cột, nút, mảng màu) ăn mất hơn nửa cột 48px, chữ
            chỉ còn 19px nên mã 4 ký tự đã phải xuống dòng. Nay mảng màu rộng
            ~43px, đủ cho `PROJECT_KEY_MAX` ký tự mono trên một dòng.
          */
            sidebarCollapsed ? 'w-12 pr-1' : 'w-52 pr-3 @2xl:w-60',
          )}
        >
          {/*
          Nội dung thanh bên **dính** ở mép trên vùng cuộn — người dùng yêu cầu
          2026-09-30: cuộn một tab dài (Báo cáo) thì danh sách dự án trôi mất lên
          trên, muốn sang dự án khác phải cuộn ngược lại.

          Cột ngoài vẫn `self-stretch` và giữ `border-r`: đường chia cột phải dài
          bằng cả trang (xem ghi chú ở `ProjectSidebar`), chỉ phần bên trong dính.
          `top-0` dừng ngay dưới thanh điều hướng vì vị trí dính tính từ mép
          trong lớp đệm của `<main>` (xem `app-shell.tsx`). Trần cao = màn hình
          trừ đệm trên + dưới của `<main>` theo từng breakpoint (58+12 / 62+16 /
          70+24px); danh sách dài hơn thế thì cuộn riêng bên trong.
        */}
          <div className="sticky top-0 flex max-h-[calc(100dvh-4.5rem)] flex-col gap-2 sm:max-h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-6rem)]">
            {/*
          "Tất cả dự án" ở **trên cùng**, ngang hàng với dải tab bên phải.

          Trước đây nó nằm dưới danh sách dự án, với lý lẽ của APM-ROLE-m2b: đó
          là chỗ người ta nhìn khi không thấy dự án mình cần. Lý lẽ ấy đúng cho
          "Dự án mới", không đúng cho đây — "Tất cả dự án" là thao tác về **cả danh
          sách**, và một nút như thế nằm sau danh sách là nằm sau thứ nó tác
          động lên.

          "Dự án mới" có ở chính màn hình ấy (cạnh Huỷ), và từ 2026-10-08 thêm một
          nút ngay dưới hàng này **trong lúc màn ấy mở** — xem ghi chú ở nút.

          `h-9` đúng bằng chiều cao `TabsList` bên phải, nên hai hàng thẳng nhau
          ở mọi bề rộng — không phải canh bằng `mt-` đoán tay.
        */}
            {/*
          Không khung bao quanh hai nút — người dùng bỏ 2026-09-28, cùng lúc với
          tấm kính của danh sách bên dưới. Hai nút vẫn mặc áo kính của dải tab
          (tròn, hover xám, "Tất cả dự án" bật thì có thấu kính).

          `h-9` đúng bằng chiều cao `TabsList` bên phải, nên hai hàng thẳng nhau.
        */}
            <div className="flex h-9 items-center gap-1">
              {/*
            Nút thu gọn đứng **trước** nút danh sách, và là nút duy nhất còn lại
            khi đã thu gọn: ở bề rộng 48px thì "Tất cả dự án" không đọc được,
            còn một mũi kép thì đọc được.

            `aria-label` nói ra việc nó làm; `«`/`»` một mình là hình, không phải
            chữ, và trình đọc màn hình đọc nó thành tên ký tự.
          */}
              <button
                type="button"
                aria-label={t(sidebarCollapsed ? 'projects.expand' : 'projects.collapse')}
                title={t(sidebarCollapsed ? 'projects.expand' : 'projects.collapse')}
                className={cn(GLASS_TAB_TRIGGER_CLASS, 'shrink-0 px-2.5')}
                onClick={toggleCollapsed}
              >
                {sidebarCollapsed ? (
                  <ChevronsRight aria-hidden className="size-4" />
                ) : (
                  <ChevronsLeft aria-hidden className="size-4" />
                )}
              </button>

              {sidebarCollapsed ? null : (
                /*
              Nút **hai chiều**: bấm lần nữa khi màn hình đang mở là Huỷ.

              `aria-pressed` là thứ nói ra trạng thái ấy cho trình đọc màn hình;
              `data-[state=on]` chỉ là màu. Một nút đang bật mà chỉ khác màu là
              một nút không có trạng thái với người không nhìn thấy màu.
            */
                <button
                  type="button"
                  aria-pressed={listEditing}
                  className={cn(PROJECT_LIST_BUTTON_CLASS, 'flex-1')}
                  data-tour="project-list-edit"
                  onClick={() => (listEditing ? leaveList() : setListEditing(true))}
                >
                  <span className={PROJECT_LIST_ICON_CLASS}>
                    <ListOrdered aria-hidden className="size-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-left">{t('projectList.edit')}</span>
                  <ChevronRight
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground group-aria-pressed/plist:text-primary"
                  />
                </button>
              )}
            </div>

            {/*
              "Dự án mới" ở **đầu** thanh bên — chỉ khi đang mở màn "Tất cả dự án"
              (người dùng chốt 2026-10-08). Ngoài màn ấy thanh bên là chỗ mở dự
              án, không có nút tạo: một nút thường trực cạnh tranh chỗ với chính
              danh sách. Chưa có dự án nào thì đường tạo nằm ở nhánh riêng phía
              trên (`ProjectSidebar.onCreate`).

              Viên màu chính, đứng **ngoài** vùng cuộn nên danh sách dài tới đâu
              nó vẫn ở đó.

              Không mang `data-tour="project-create"`: chặng tour ấy chỉ vào nút
              ở hàng đầu bảng, hai neo cùng tên là tour sáng nhầm nút.
            */}
            {canCreateProject && listEditing ? (
              sidebarCollapsed ? (
                <Button
                  size="icon"
                  className="shrink-0"
                  aria-label={t('createProject.title')}
                  title={t('createProject.title')}
                  onClick={() => setCreating(true)}
                >
                  <FolderPlus aria-hidden />
                </Button>
              ) : (
                <Button
                  className="w-full shrink-0 gap-1.5 font-medium"
                  onClick={() => setCreating(true)}
                >
                  <FolderPlus aria-hidden />
                  <span className="min-w-0 truncate">{t('createProject.title')}</span>
                </Button>
              )
            ) : null}

            {/*
              Danh sách dài hơn chiều cao màn hình thì cuộn **trong** thanh bên.

              `overflow-x-hidden` phải viết ra: `overflow-y-auto` một mình làm trình
              duyệt tính trục kia thành `auto` (CSS không cho `visible` đứng cạnh
              một trục cuộn), và danh sách bên trong lấn 4px (`-mr-1`, chỗ cho
              thanh cuộn dọc của nó) — đủ để hiện một thanh trượt ngang (2026-10-01).
            */}
            <div
              className={cn(
                'scroll-autohide min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
                // Thu gọn: thanh cuộn không chiếm chỗ — cùng lý do ở `<ul>` của `ProjectSidebar`.
                sidebarCollapsed && '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              )}
            >
              <ProjectSidebar
                projects={sidebarProjects}
                /*
            Màn sắp xếp **không** chọn dự án nào — người dùng yêu cầu
            2026-09-28: ở đây danh sách là thứ để sắp, không phải để mở. Dự án
            đang xem vẫn nằm nguyên trên URL (cú bấm vào thanh bên đã bị khoá
            lúc sắp), nên Hoàn thành hay Huỷ là viên trượt hiện lại đúng chỗ cũ
            mà không phải nhớ gì thêm.
          */
                selectedId={listEditing ? null : selected.id}
                rolesByProject={rolesByProject}
                onSelect={pick}
                editing={listEditing}
                onSetHidden={setHidden}
                {...(canCreateProject ? { onCreate: () => setCreating(true) } : {})}
                collapsed={sidebarCollapsed}
                insertBefore={insertBefore ? { beforeId: insertBefore.beforeId } : null}
                allCount={projects.data.length}
              />
            </div>
          </div>
        </div>
      )}

      {narrow ? (
        <ProjectDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <button
            type="button"
            // Cùng neo, cùng trạng thái với nút ở thanh bên: tour bấm hộ nút này trên màn hẹp
            // (`findTourAnchor` chọn bản đang hiện, APM-MBAR-c).
            aria-pressed={listEditing}
            data-tour="project-list-edit"
            className={cn(PROJECT_LIST_BUTTON_CLASS, 'h-10 w-full shrink-0')}
            onClick={() => {
              setDrawerOpen(false);
              setListEditing(true);
            }}
          >
            <span className={PROJECT_LIST_ICON_CLASS}>
              <ListOrdered aria-hidden className="size-3.5" />
            </span>
            <span className="min-w-0 flex-1 truncate text-left">{t('projectList.edit')}</span>
            <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          </button>
          {/* `overflow-x-hidden`: cùng lý do với thanh bên ở trên. */}
          <div className="scroll-autohide min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <ProjectSidebar
              projects={sidebarProjects}
              selectedId={selected.id}
              rolesByProject={rolesByProject}
              onSelect={(projectId) => {
                pick(projectId);
                setDrawerOpen(false);
              }}
              {...(canCreateProject
                ? {
                    onCreate: () => {
                      setDrawerOpen(false);
                      setCreating(true);
                    },
                  }
                : {})}
              allCount={projects.data.length}
            />
          </div>
        </ProjectDrawer>
      ) : null}

      {/* `min-w-0` để board nhiều cột cuộn ngang trong vùng này thay vì đẩy
          sidebar dự án ra khỏi màn hình. */}
      {/*
        Cột này là `@container` của riêng nó — APM-PSET-f.

        Bề rộng của nó đổi theo **hai** thứ: cửa sổ, và việc người dùng gấp thanh
        bên (240px → 48px). Container của `main` chỉ thấy thứ nhất. Khai thêm một
        container ở đây để mọi khối bên trong (`@…:` bám container gần nhất) xếp
        lại đúng lúc có thêm chỗ — chính là điều người dùng báo là còn thiếu.
      */}
      <div className="@container flex min-w-0 flex-1 flex-col gap-4">
        {listEditing ? <PersonalSpaceCard /> : null}
        {listEditing && narrow ? (
          <ProjectDirectoryCompact
            rows={directory.data?.rows ?? []}
            order={tableOrderNow}
            hidden={hiddenNow}
            onToggle={setHidden}
            roles={rolesByProject}
            colors={new Map(projects.data.map((project) => [project.id, project.color]))}
            {...(canCreateProject ? { onCreate: () => setCreating(true) } : {})}
            onOpen={(projectId) => {
              pick(projectId);
              leaveList();
            }}
            saving={saveSidebar.isPending}
            onFinish={() =>
              saveSidebar.mutate({
                hidden: [...hiddenNow],
                order: orderNow,
                tableOrder: directorySequence(directory.data?.rows ?? [], tableOrderNow),
              })
            }
            onCancel={leaveList}
            asGuest={asGuest}
            quickAccessCount={sidebarProjects.length}
            quickAccess={
              // Cùng thanh bên ở chế độ sửa như cột trái của màn rộng: kéo để sắp, thùng rác để bỏ.
              <ProjectSidebar
                projects={sidebarProjects}
                selectedId={null}
                rolesByProject={rolesByProject}
                onSelect={pick}
                editing
                onSetHidden={setHidden}
                allCount={projects.data.length}
              />
            }
          />
        ) : listEditing ? (
          /*
            Màn "Tất cả dự án" **thay** cả vùng tab — APM-PSET-c.

            Thao tác chính là kéo một dòng sang sidebar bên trái, nên sidebar
            phải còn nhìn thấy; một hộp thoại phủ lên sẽ che đúng cái đích của
            cú kéo. Đổi lại, vùng tab mất đi trong lúc sửa — và đó là điều người
            dùng yêu cầu.
          */
          <ProjectDirectoryScreen
            rows={directory.data?.rows ?? []}
            order={tableOrderNow}
            hidden={hiddenNow}
            onToggle={setHidden}
            roles={rolesByProject}
            {...(canCreateProject ? { onCreate: () => setCreating(true) } : {})}
            onOpen={(projectId) => {
              // Mở dự án **và** thoát màn hình: cùng một lối ra với Huỷ, nên
              // quy tắc chỉ có một câu — ra khỏi đây bằng đường nào ngoài
              // "Hoàn thành" thì bản nháp bị bỏ.
              pick(projectId);
              leaveList();
            }}
            saving={saveSidebar.isPending}
            onFinish={() =>
              saveSidebar.mutate({
                hidden: [...hiddenNow],
                order: orderNow,
                tableOrder: directorySequence(directory.data?.rows ?? [], tableOrderNow),
              })
            }
            onCancel={leaveList}
            asGuest={asGuest}
          />
        ) : (
          <>
            {/*
              Một dòng, một lần cho cả trang — APM-PSPACE-b. Nút mời / tải lên / thêm tài liệu và ba
              tab đã **ẩn** (không làm mờ), nên đây là chỗ duy nhất nói vì sao chúng không có.
            */}
            {orgIsPersonal ? (
              <p className="text-xs text-muted-foreground" data-testid="personal-space-notice">
                {t('personalSpaceLimits.notice', { limit: PERSONAL_SPACE_MAX_TASKS })}
              </p>
            ) : null}
            <header className="flex flex-wrap items-center gap-3">
              {/* Tên dự án đã hiện ở `ProjectSidebar` bên trái — lặp lại ở đây chỉ
              tốn chỗ mà không thêm thông tin gì mới. */}
              <Tabs
                /*
              ⚠️ `w-full` — APM-ORG-h6.

              `Tabs` là `TabsPrimitive.Root`, một `<div>` bình thường **co theo
              nội dung**. Không có lớp này thì `justify-between` bên trong không
              có gì để dàn ra: hộp chỉ rộng bằng hai `TabsList` cộng lại, nên
              nhóm tiện ích dính ngay sau tab cuối ("Nhân sự") thay vì bám lề
              phải. Trông y như bố cục đúng cho tới khi cửa sổ rộng hơn.
            */
                className="w-full"
                value={tab}
                onValueChange={(value) =>
                  void navigate({
                    /*
                  Ép kiểu phải gồm **cả hai** nhóm — APM-ROLE-v.

                  `tabs` nay chỉ còn bảy tab của dự án; "Tua lại" và "Cấu hình"
                  đã tách sang `utilityTabs`. Giữ nguyên ép kiểu cũ thì nó khẳng
                  định một điều không đúng: TypeScript tin `value` không bao giờ
                  là `'settings'`, trong khi bấm vào tab ấy thì nó chính là thế.
                */
                    search: {
                      ...search,
                      tab: value as (typeof tabs)[number] | (typeof utilityTabs)[number],
                    },
                  })
                }
              >
                {/*
              `justify-between` **và** `ml-auto` — APM-ORG-h6.

              Bản trước cố ý chỉ dùng `justify-between`, với lý lẽ: một `ml-auto`
              dính vào nhóm phải như thể nó là thuộc tính của nhóm ấy chứ không
              phải của quan hệ giữa hai nhóm. Lý lẽ đúng về mặt diễn đạt, nhưng
              nó không sống sót qua `flex-wrap`: khi hàng xuống dòng, dòng thứ
              hai chỉ có một phần tử và `justify-between` đẩy nó về **trái**.

              Hai lớp phủ hai trường hợp khác nhau, nên giữ cả hai.
            */}
                {/*
                  Dải tab tự thu gọn — APM-PSET-g.

                  Thứ tự nhường chỗ do người dùng đặt (2026-09-18): bỏ chữ hai
                  nút tiện ích trước, rồi đẩy tab vào menu từ phải sang, cuối
                  cùng mới gấp thanh bên. Phép tính ở `tab-fit.ts`, phép đo ở
                  `ProjectTabsRow` — ở đây chỉ còn cách vẽ từng mảnh.
                */}
                <ProjectTabsRow
                  tabs={tabs}
                  utilityTabs={utilityTabs}
                  activeTab={tab}
                  onSelectTab={(value) => void navigate({ search: { ...search, tab: value } })}
                  onNeedSidebar={setSidebarSqueezed}
                  /*
                    Cùng cửa quyền với mục "Thanh tab" ở trang Cấu hình
                    (`project:update`): hộp mở ra là **chính** mục ấy, nên một
                    người mở được ở đây mà không lưu được ở kia là hai màn hình
                    nói hai chuyện về cùng một quyền.
                  */
                  onEditTabs={canEditColumns ? () => setEditingTabs(true) : undefined}
                  stacked={narrow}
                  leading={
                    <ProjectSwitcherButton
                      projectKey={selected.key}
                      name={selected.name}
                      color={selected.color}
                      onClick={() => setDrawerOpen(true)}
                    />
                  }
                  /*
                    Màu dự án tô **nền** tab Điều lệ — APM-PSET-e.

                    Chỉ tab ấy, vì chỉ nó nói về *dự án nào*; các tab kia nói về
                    mặt nào của dự án và tô màu chúng là biến cả dải tab thành
                    một mảng màu không phân biệt được gì.
                  */
                  tabClassName={(value) =>
                    // Bàn tay chỉ ở tab Điều lệ — người dùng yêu cầu 2026-10-14.
                    value === 'overview'
                      ? cn('cursor-pointer', tabColorClass(selected.color))
                      : undefined
                  }
                  /*
                    Tab Điều lệ mang **mã + tên dự án**, không mang một nhãn cố
                    định — APM-PSET-d. Nó là tab đầu tiên và là chỗ mắt dừng lại
                    trước nhất, nên nó trả lời "đang mở dự án nào" mà không phải
                    thêm một dòng tiêu đề nữa.
                  */
                  renderTab={(value) =>
                    // Màn hẹp: tên dự án đã ở nút chọn dự án ngay phía trên.
                    value === 'overview' && !narrow ? (
                      <ProjectTabLabel projectKey={selected.key} name={selected.name} />
                    ) : (
                      t(`board.tab.${value}`)
                    )
                  }
                  // Trong menu không vẽ được nhãn nhiều tầng, nên tab Điều lệ
                  // rút về "MÃ · Tên" — đúng thứ `ProjectTabLabel` để ở `title`.
                  tabText={(value) =>
                    value === 'overview'
                      ? `${selected.key} · ${selected.name}`
                      : t(`board.tab.${value}`)
                  }
                  /*
                    Màu riêng cho nhóm tiện ích, không phải xám như dải tab —
                    APM-ORG-h6. Nó nói "đây không phải một cách nhìn dự án": một
                    cái là cỗ máy thời gian, một cái là chỗ chỉnh công cụ. Dùng
                    token `tag-8` chứ không phải RAG — khối này không mang nghĩa
                    tốt/xấu, và một viền vàng ở đây sẽ đọc thành cảnh báo.
                  */
                  // Không tô nền tab đang mở: dải kính đã có viên trượt làm việc ấy.
                  utilityClassName={() => 'gap-1.5 text-tag-8 data-[state=active]:text-tag-8'}
                  utilityContent={(value, compact) => {
                    /*
                      Icon **chỉ** ở nhóm này, không ở dải tab bên trái —
                      APM-ORG-h6b. Các tab kia là những mặt của dự án và chúng
                      ngang hàng nhau; gắn icon cho cả dãy là thêm nhiễu chứ
                      không thêm nghĩa. Ở đây icon là kênh thứ hai nói rằng nhóm
                      này khác loại — và khi hàng chật, nó là kênh **duy nhất**
                      còn lại, nên nó phải có sẵn từ đầu chứ không xuất hiện lúc
                      chữ biến mất.
                    */
                    const Icon = value === 'replay' ? History : SlidersHorizontal;

                    return (
                      <>
                        <Icon aria-hidden className="size-3.5" />
                        {/*
                          Chữ đi trước tiên khi hàng chật, nhưng `title` ở lại:
                          một nút chỉ còn icon mà không có tên là một câu đố.
                        */}
                        {compact ? (
                          <span className="sr-only">{t(`board.tab.${value}`)}</span>
                        ) : (
                          t(`board.tab.${value}`)
                        )}
                        {/*
                          Badge đỏ đếm việc PM còn phải làm — APM-PSET-b. Nó
                          **không** biến mất khi thu gọn: đó là con số nói "còn
                          thứ chưa cấu hình", và giấu nó đi là giấu đúng lý do
                          người ta cần bấm vào.
                        */}
                        {value === 'settings' ? <SetupBadge projectId={selected.id} /> : null}
                      </>
                    );
                  }}
                />
              </Tabs>

              {/*
            Nhãn DEMO và dòng "Phương pháp" đã rời khỏi đây — APM-ROLE-t.

            Nhãn DEMO về **sidebar**, cạnh tên dự án: đó là nơi người ta chọn dự
            án, nên nó cảnh báo *trước* khi người dùng bước vào, chứ không phải
            nhắc lại sau khi đã vào rồi. Comment cũ ở đây viện dẫn spec 18.2 để
            biện minh cho việc lặp ở mọi tab; đã xác nhận là viện dẫn sai và xoá
            cùng lúc — giữ lại chỉ để lần sau có người tin nó lần nữa.

            Phương pháp về tab Tổng quan: nó là một **tính chất của dự án**,
            không phải chú thích của mọi màn hình.
          */}
            </header>

            {tab === 'overview' ? (
              search.sheet ? (
                <DecisionSheetPage
                  key={search.sheet}
                  projectId={selected.id}
                  sheetId={search.sheet}
                  asOf={search.sheetAsOf}
                  mode={search.sheetMode}
                  onModeChange={(sheetMode) => void navigate({ search: { ...search, sheetMode } })}
                  // Mở xem trước tài liệu đã xuất ⇒ `?doc=` (APM-PVLINK): Back đóng popup.
                  previewDoc={search.doc}
                  onPreviewDocChange={(doc) =>
                    void navigate({ search: { ...search, doc }, replace: doc === undefined })
                  }
                  onBack={() =>
                    void navigate({
                      search: {
                        ...search,
                        sheet: undefined,
                        sheetAsOf: undefined,
                        sheetMode: undefined,
                      },
                    })
                  }
                  onAsOfChange={(sheetAsOf) => void navigate({ search: { ...search, sheetAsOf } })}
                  onOpenRaid={() =>
                    void navigate({
                      search: {
                        ...search,
                        tab: 'raid',
                        sheet: undefined,
                        sheetAsOf: undefined,
                        sheetMode: undefined,
                      },
                    })
                  }
                />
              ) : search.files ? (
                <ProjectFilesPage projectId={selected.id} />
              ) : (
                <OverviewPanel project={selected} />
              )
            ) : tab === 'sprints' ? (
              <SprintTabPanel
                projectId={selected.id}
                taskTypeId={selected.taskTypes[0]?.id ?? null}
                canEdit={canEditColumns}
                // Bấm một sprint là sang board của đúng sprint đó — "chi tiết một
                // sprint" nghĩa là thấy việc của nó, không phải thấy thêm số liệu.
                onOpenSprint={(sprintId) =>
                  void navigate({
                    search: { ...search, tab: 'tasks', taskView: 'board', sprintId },
                  })
                }
              />
            ) : tab === 'tasks' ? (
              <TasksPanel
                projectId={selected.id}
                view={search.taskView ?? 'board'}
                onViewChange={(taskView) => void navigate({ search: { ...search, taskView } })}
                // Bỏ trống = sprint đang chạy. Có giá trị = người dùng vừa bấm một
                // sprint ở tab Sprint, kể cả sprint đã đóng.
                sprintId={search.sprintId}
                onSprintChange={(sprintId) =>
                  void navigate({ search: { ...search, sprintId: sprintId ?? undefined } })
                }
                // Waterfall không có sprint nào, nên board của nó là "mọi task".
                waterfall={selected.methodology === 'WATERFALL'}
                maintenance={selected.methodology === 'MAINTAIN'}
                period={activePeriod ?? undefined}
                onPeriodChange={(period) => void navigate({ search: { ...search, period } })}
                deadline={selected.endDate}
                canEditColumns={canEditColumns}
                canManageColumns={canManageColumns}
                canEditTasks={canEditTasks}
                canAssign={canAssign}
                canApproveSuggestions={canApproveSuggestions}
                openedTaskId={search.taskId ?? null}
                onOpenedTaskChange={(taskId) =>
                  void navigate({ search: { ...search, taskId: taskId ?? undefined } })
                }
              />
            ) : tab === 'issues' ? (
              <IssuePanel
                projectId={selected.id}
                openClients={search.section === 'client-requests' ? 'pending' : undefined}
                openIssueId={search.issueId}
                onOpenIssue={(issueId) =>
                  void navigate({ search: { ...search, issueId: issueId ?? undefined } })
                }
              />
            ) : tab === 'raid' ? (
              <RaidPanel projectId={selected.id} />
            ) : tab === 'changes' ? (
              <ChangeRequestsPanel projectId={selected.id} project={selected} />
            ) : tab === 'coordination' ? (
              <CoordinationPanel projectId={selected.id} />
            ) : tab === 'settings' ? (
              <ProjectSettingsPanel
                project={selected}
                projectId={selected.id}
                permissions={projectPermissions}
                section={search.section}
              />
            ) : tab === 'reports' ? (
              <ReportsPanel
                projectId={selected.id}
                asOf={search.asOf}
                group={search.chart ?? 'summary'}
                // Dùng chung `sprintId` với tab Công việc — APM-REPORT-c. Đổi tab
                // không mất sprint đang xem, và một link dán cho nhau mở ra đúng
                // thứ người gửi đang nhìn.
                sprintId={search.sprintId}
                canEditTasks={canEditTasks}
                period={activePeriod}
                onPeriodChange={(period) => void navigate({ search: { ...search, period } })}
                onChange={(next) =>
                  void navigate({
                    search: {
                      ...search,
                      tab: 'reports',
                      ...(next.group ? { chart: next.group } : {}),
                      ...('sprintId' in next ? { sprintId: next.sprintId } : {}),
                    },
                  })
                }
              />
            ) : tab === 'budget' ? (
              <BudgetPanel
                projectId={selected.id}
                // Dùng chung `asOf` với tab Báo cáo — luật #7. Một mốc thời gian cho
                // cả hai, nên link chia sẻ mở ra đúng cảnh người gửi đang nhìn.
                asOf={search.asOf}
                canManage={canManageBudget}
                canManageRates={canManageCostRates}
              />
            ) : tab === 'replay' ? (
              <ReplayPanel
                projectId={selected.id}
                at={search.at}
                view={search.view}
                compareAt={search.compare}
                play={search.play === true}
                onNavigate={(next) =>
                  void navigate({
                    search: {
                      ...search,
                      tab: 'replay',
                      at: next.at,
                      view: next.view,
                      compare: next.compareAt,
                      play: next.play ? true : undefined,
                    },
                  })
                }
              />
            ) : tab === 'design' ? (
              <DesignPanel
                projectId={selected.id}
                canWrite={canWriteDesign}
                fileId={search.designFile}
                snapshotId={search.designSnapshot}
                screenNodeId={search.designScreen}
                compare={search.designCompare}
                onNavigate={(next) =>
                  void navigate({
                    search: {
                      ...search,
                      designFile: next.fileId,
                      designSnapshot: next.snapshotId,
                      designScreen: next.screenNodeId,
                      designCompare: next.compare,
                    },
                  })
                }
              />
            ) : tab === 'git' ? (
              <GitPanel
                projectId={selected.id}
                // Dùng chung `asOf` với tab Báo cáo, Ngân sách và Kiểm thử — luật #7.
                asOf={search.asOf}
                view={search.gitView}
                changeRequest={search.gitCr}
                release={search.gitRelease}
                onNavigate={(next) =>
                  void navigate({
                    search: {
                      ...search,
                      gitView: next.view,
                      gitCr: next.changeRequest,
                      gitRelease: next.release,
                    },
                  })
                }
                // Cấu hình Git ở Cấu hình dự án → Kho mã Git (APM-GIT-o). `replace` khi
                // chuyển link cũ `?gitView=settings`, để Back không quay lại nó.
                onOpenSettings={(options) =>
                  void navigate({
                    search: {
                      ...search,
                      tab: 'settings',
                      section: 'git',
                      gitView: undefined,
                      gitCr: undefined,
                      gitRelease: undefined,
                    },
                    replace: options?.replace === true,
                  })
                }
              />
            ) : tab === 'testing' ? (
              <TestingPanel
                projectId={selected.id}
                // Dùng chung `asOf` với tab Báo cáo và Ngân sách — luật #7. Một mốc
                // cho cả ba, nên link chia sẻ mở ra đúng cảnh người gửi đang nhìn.
                asOf={search.asOf}
                // Vòng đang xem ở URL, không ở state của panel — APM-TEST-h: vạch
                // mốc trên Gantt bấm sang đây và phải mở đúng vòng ấy.
                roundId={search.roundId}
                onPickRound={(roundId) => void navigate({ search: { ...search, roundId } })}
                // Lỗi đang mở ngăn chi tiết — đích của thông báo lỗi và của "Việc
                // của tôi" (APM-TEST2-b/-e). Ở URL để link mở đúng lỗi.
                bugId={search.bug}
                onOpenBug={(bug) => void navigate({ search: { ...search, bug } })}
                canExecute={canExecuteTests}
                canManage={canManageTests}
                taskTypes={selected.taskTypes}
                canCreateTask={canCreateTask}
              />
            ) : (
              /* Mọi giá trị của enum đều có nhánh riêng ở trên, nên đây chỉ là chỗ
             đỡ cho một `tab` lạ lọt qua. Về Tổng quan — cùng đích với
             `.catch()` của schema, chứ không phải một mặc định thứ hai. */
              <OverviewPanel project={selected} />
            )}
          </>
        )}

        {/*
          Hộp tạo dự án nằm **ngoài** nhánh `listEditing`, nên nó mở được từ cả
          hai chế độ.

          Trước đây nó nằm trong nhánh "không sửa danh sách", nên nút "Dự án
          mới" vừa chuyển vào màn hình "Tất cả dự án" bấm xong **không mở gì cả**:
          `creating` thành `true` mà không chỗ nào render hộp. Không lỗi, không
          dấu hiệu — chỉ là một cú bấm không có tác dụng. Nó là lớp phủ nên chỗ
          đứng trong cây không ảnh hưởng gì tới bố cục.
        */}
        {creating ? (
          <CreateProjectDialog
            onDone={() => setCreating(false)}
            onOpenProject={openCreatedProject}
          />
        ) : null}
        {/*
          Hộp sắp thanh tab — **cùng một component** với mục ở trang Cấu hình.

          Không viết bản rút gọn: hai bản của cùng một màn hình là hai chỗ phải
          sửa mỗi lần đổi luật (tab trụ, tab mới lên sóng, cửa quyền), và chúng
          sẽ lệch nhau ở lần sửa thứ hai. Đây đúng là lý lẽ đã ghi cho
          `ObjectivesPanel` ở tab Tổng quan.

          `key` theo dự án: bản nháp bên trong là state cục bộ và nó không tự
          biết mình đang nói về một dự án khác khi người dùng đổi dự án.
        */}
        {editingTabs ? (
          <Dialog label={t('tabLayout.title')} onClose={() => setEditingTabs(false)}>
            <ProjectTabLayoutSection
              key={selected.id}
              project={selected}
              /*
                ⚠️ `projectPermissions`, **không** phải `abilities.global`.

                Quyền đọc Báo cáo / Ngân sách / Kiểm thử / Thiết kế của một
                LEADER là quyền **theo dự án**, không nằm trong danh sách toàn
                cục. Truyền nhầm danh sách thì hộp chỉ liệt kê 5 tab trong khi
                dải tab đang hiện 10 — đo thấy đúng 5/10 trước khi sửa. Năm tab
                vắng mặt không bị ẩn (`layoutOf` chỉ ghi những dòng nó thấy,
                và id không có mặt nghĩa là "để nguyên"), nhưng chúng cũng
                không sắp được, và cái hộp nói sai về chính dải tab bên cạnh.
              */
              permissions={projectPermissions}
              onSaved={() => setEditingTabs(false)}
            />
          </Dialog>
        ) : null}
      </div>
    </div>
  );

  /*
    `DndContext` bọc **cả hai cột** — APM-PSET-c.

    Một dòng bảng ở cột phải phải kéo được sang thanh bên ở cột trái, nên cả hai
    phải nằm trong cùng một context. Chỉ bọc khi đang sửa: một `DndContext`
    thường trực sẽ bắt pointer event trên cả trang cho một tính năng chỉ dùng
    trong lúc sắp danh sách.
  */
  if (!listEditing) return layout;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={dropUnderPointer}
      onDragOver={(event) => onDrag(event, false)}
      onDragEnd={(event) => {
        // Bản xem trước đã là nước đi; chốt lại đúng nó để không lệch một nhịp.
        onDrag(event, true);
        setInsertBefore(null);
      }}
      onDragCancel={() => setInsertBefore(null)}
    >
      {layout}
    </DndContext>
  );
}
